import { Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowRight,
  BookUser,
  Building2,
  Camera,
  Check,
  ChevronLeft,
  Coins,
  FileText,
  Loader2,
  MessageSquare,
  Package,
  Phone,
  PhoneCall,
  Sheet,
  Truck,
  User,
} from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import {
  isFreeMessagesOfferActive,
  TEMPLATE_SEND_COST,
  type ShipmentDetailsInput,
  type ShippingLabelDataDto,
  type TemplateDto,
} from "@mandoob/shared";

import { useStartConversation } from "@/api/inbox";
import { useGetTemplates } from "@/api/templates";
import { ResponsiveDialog } from "@/components/shared/responsive-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useLanguage } from "@/contexts/language";
import { api } from "@/lib/api-client";
import { isNative } from "@/lib/capacitor";
import { errorMessage } from "@/lib/errors";
import {
  formatPhoneNumberForDisplay,
  isDriverOwnNumber,
  normalizePhoneInput,
} from "@/lib/phone-utils";
import { cn, getErrorCode } from "@/lib/utils";
import { loadSession, useSession } from "@/session";

import {
  buildTemplateVariables,
  FIELD_LABEL_KEYS,
  fillTemplateBody,
  templateFields,
  templateNeeds,
  type FieldKey,
} from "./template-fields";
import {
  ContactsPermissionError,
  contactsSourceAvailable,
  pickDeviceContacts,
  stashContacts,
  type PickedContact,
} from "./import/contacts";
import { ContactsPickerDialog } from "./import/contacts-picker-dialog";
import { formatTemplateName } from "./lib";
import { prepareLabelImage } from "./media";
import { WhatsAppText } from "./whatsapp-text";

/** The last-used template is restored as the default on the next shipment. */
const LAST_TEMPLATE_KEY = "mandoob:lastTemplateId";

function readLastTemplateId(): string | null {
  try {
    return localStorage.getItem(LAST_TEMPLATE_KEY);
  } catch {
    return null;
  }
}

function saveLastTemplateId(id: string) {
  try {
    localStorage.setItem(LAST_TEMPLATE_KEY, id);
  } catch {
    // Storage unavailable — the selection just won't be remembered.
  }
}

/**
 * The optional shipment facts a label carried, as the start-conversation
 * input expects them: only the keys the OCR actually found. They ride along
 * with the auto-send and are stored on the delivery / client silently.
 */
function labelDetails(data: ShippingLabelDataDto): ShipmentDetailsInput {
  const details: ShipmentDetailsInput = {};
  if (data.paymentType) details.paymentType = data.paymentType;
  if (data.costHalalas != null) details.costHalalas = data.costHalalas;
  if (data.shippingCostHalalas != null) {
    details.shippingCostHalalas = data.shippingCostHalalas;
  }
  if (data.senderName) details.senderName = data.senderName;
  if (data.senderType) details.senderType = data.senderType;
  if (data.senderWebsite) details.senderWebsite = data.senderWebsite;
  if (data.weightGrams != null) details.weightGrams = data.weightGrams;
  if (data.nationalAddress) details.nationalAddress = data.nationalAddress;
  if (data.city) details.clientCity = data.city;
  return details;
}

export function NewDeliveryPage({
  initialPhone,
}: {
  /** E.164 digits (no +) from a WhatsApp link — prefills the client phone. */
  initialPhone?: string;
} = {}) {
  const { t, dir } = useLanguage();
  const navigate = useNavigate();
  const session = useSession();
  const driver = session?.driver;

  // Template — the saved choice (or the first template) is preselected; the
  // card on the form opens a full-screen picker to change it.
  const templatesQuery = useGetTemplates({ MaxResultCount: 100 });
  const templates = useMemo(
    () =>
      (templatesQuery.data?.items ?? []).filter(
        (tpl) => !tpl.hasMediaHeader && tpl.status === "Approved",
      ),
    [templatesQuery.data],
  );
  const [templateId, setTemplateId] = useState<string | null>(
    readLastTemplateId,
  );
  const [pickingTemplate, setPickingTemplate] = useState(false);

  // "From contacts" runs the whole picking flow HERE (permission, book read,
  // selection dialog); only the final selection travels to the import page.
  const [pickingContacts, setPickingContacts] = useState(false);
  const [deviceContacts, setDeviceContacts] = useState<PickedContact[]>([]);
  const [contactsPickerOpen, setContactsPickerOpen] = useState(false);

  const goToImportWith = (contacts: PickedContact[]) => {
    setContactsPickerOpen(false);
    if (contacts.length === 0) return;
    stashContacts(contacts);
    void navigate({ to: "/chats/import", search: { source: "contacts" } });
  };

  const handlePickContacts = async () => {
    if (pickingContacts) return;
    setPickingContacts(true);
    try {
      const result = await pickDeviceContacts();
      if (!result) return;
      if (result.contacts.length === 0) {
        toast.error(t("Mandoob:Import.ContactsEmpty"));
        return;
      }
      if (result.mode === "picked") {
        goToImportWith(result.contacts);
      } else {
        setDeviceContacts(result.contacts);
        setContactsPickerOpen(true);
      }
    } catch (error) {
      toast.error(
        error instanceof ContactsPermissionError
          ? t("Mandoob:Import.ContactsPermission")
          : errorMessage(error, t),
      );
    } finally {
      setPickingContacts(false);
    }
  };
  const template: TemplateDto | null =
    templates.find((tpl) => tpl.id === templateId) ?? templates[0] ?? null;
  const needs = template ? templateNeeds(template) : null;
  // The tracking-number input only exists when the chosen template's body
  // actually carries the tracking number.
  const usesTracking =
    !!template && templateFields(template).includes("trackingNumber");
  // Whether the message-details card has any field to show at all.
  const hasMessageFields = !!(
    needs &&
    (!needs.mapped
      ? template!.variables.length > 0
      : needs.company || needs.driverPhone || needs.driverName)
  );

  // Client + shipment.
  const [clientPhone, setClientPhone] = useState(
    initialPhone ? `+${initialPhone}` : "",
  );
  const [clientPhoneError, setClientPhoneError] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [trackingError, setTrackingError] = useState(false);
  const [clientName, setClientName] = useState("");

  // Template data — pre-filled from the driver profile, editable per shipment.
  const [company, setCompany] = useState(driver?.company ?? "");
  const [driverPhone, setDriverPhone] = useState(
    driver?.callingPhone ?? driver?.phone ?? "",
  );
  const [driverPhoneError, setDriverPhoneError] = useState(false);
  const [driverName, setDriverName] = useState(driver?.name ?? "");
  // Fallback for a template whose params don't follow the field convention.
  const [genericValues, setGenericValues] = useState<Record<string, string>>(
    {},
  );

  // Shipping-label capture: native camera photo -> Gemini reads the fields ->
  // the delivery is created and the template sent in one go.
  const labelInputRef = useRef<HTMLInputElement>(null);
  const labelModeRef = useRef(false);
  const [labelLoading, setLabelLoading] = useState(false);
  const [labelDoneOpen, setLabelDoneOpen] = useState(false);

  const startConversation = useStartConversation({
    mutation: {
      onSuccess: (conversation) => {
        // The first template send debited a credit.
        void loadSession().catch(() => undefined);
        // Label flow: stay here and offer "another / done" instead of
        // jumping into the chat.
        if (labelModeRef.current) {
          labelModeRef.current = false;
          setLabelLoading(false);
          setLabelDoneOpen(true);
          return;
        }
        void navigate({
          to: "/chats/$conversationId",
          params: { conversationId: conversation.id },
        });
      },
      onError: (error) => {
        if (labelModeRef.current) {
          labelModeRef.current = false;
          setLabelLoading(false);
        }
        const code = getErrorCode(error);
        if (code === "Waba:ClientBusyWithOtherDriver") {
          toast.error(t("Mandoob:NewDelivery.ClientBusy"));
          return;
        }
        if (code === "Waba:ClientHasActiveConversation") {
          toast.error(t("Mandoob:NewDelivery.ClientActive"));
          return;
        }
        if (code === "Credits:Insufficient") {
          toast.error(t("Mandoob:Credits.InsufficientToast"));
          return;
        }
        toast.error(errorMessage(error, t));
      },
    },
  });

  const selectTemplate = (id: string) => {
    setTemplateId(id);
    setGenericValues({});
    saveLastTemplateId(id);
    setPickingTemplate(false);
  };

  // Everything the templates can pull from the form, ready for previews.
  const shipmentData = {
    clientName,
    trackingNumber,
    company,
    driverPhone: formatPhoneNumberForDisplay(driverPhone),
    driverName,
  };
  const fieldLabelFor = (field: FieldKey) => t(FIELD_LABEL_KEYS[field]);

  /** Body with real data filled in; empty fields render as «label». */
  const filledBody = (tpl: TemplateDto): string => {
    const filled = fillTemplateBody(tpl, shipmentData, fieldLabelFor);
    if (filled !== tpl.bodyText || tpl.variables.length === 0) return filled;
    // Unmapped template: fill from the generic per-variable inputs instead.
    let body = tpl.bodyText;
    for (const name of tpl.variables) {
      const value = genericValues[name]?.trim();
      if (value) body = body.replaceAll(`{{${name}}}`, value);
    }
    return body;
  };

  /** Chips naming the dynamic fields a template's body can use. */
  const templateTags = (tpl: TemplateDto) => {
    const fields = templateFields(tpl);
    if (fields.length === 0) return null;
    return (
      <span className="tw:flex tw:flex-wrap tw:gap-1">
        {fields.map((field) => (
          <span
            key={field}
            className="tw:rounded-full tw:bg-brand-100 tw:px-2 tw:py-0.5 tw:text-[10px] tw:font-medium tw:text-brand-600"
          >
            {fieldLabelFor(field)}
          </span>
        ))}
      </span>
    );
  };

  // Server-enforced too; the early guard just avoids a doomed send attempt.
  // Verification builds hide all payment surfaces, the credit gate included.
  const paymentsDisabled = !!session?.appPaymentsDisabled && isNative();
  // Free-messages offer: a new shipment's template is one of the free sends.
  const offerActive = isFreeMessagesOfferActive();
  const hasCredits =
    paymentsDisabled ||
    !driver ||
    offerActive ||
    (driver.creditsBalance ?? 0) >= TEMPLATE_SEND_COST;

  const canSend =
    !!template &&
    !!needs &&
    hasCredits &&
    clientPhone.trim().length > 0 &&
    (!usesTracking || trackingNumber.trim().length > 0) &&
    (needs.mapped
      ? (!needs.company || company.trim().length > 0) &&
        (!needs.driverPhone || driverPhone.trim().length > 0) &&
        (!needs.driverName || driverName.trim().length > 0)
      : template.variables.every(
          (name) => (genericValues[name]?.trim() ?? "") !== "",
        ));

  /**
   * Kicks off the send. `overrides` lets the label-capture flow pass the
   * just-extracted values without waiting for a state round-trip. Returns
   * whether the mutation was actually started.
   */
  const handleSend = (overrides?: {
    clientPhone?: string;
    clientName?: string;
    trackingNumber?: string;
    company?: string;
    /** Extras the label carried — stored on the delivery, never shown here. */
    details?: ShipmentDetailsInput;
  }): boolean => {
    if (!template || !needs || startConversation.isPending) return false;
    const clientPhoneValue = overrides?.clientPhone ?? clientPhone;
    const clientNameValue = overrides?.clientName ?? clientName;
    const trackingValue = overrides?.trackingNumber ?? trackingNumber;
    const companyRaw = overrides?.company ?? company;

    const phone = normalizePhoneInput(clientPhoneValue);
    if (!phone) {
      setClientPhoneError(true);
      toast.error(t("Mandoob:Form.InvalidPhone"));
      return false;
    }
    if (isDriverOwnNumber(driver, phone)) {
      setClientPhoneError(true);
      toast.error(t("Mandoob:NewDelivery.CannotMessageSelf"));
      return false;
    }
    if (usesTracking && !trackingValue.trim()) {
      setTrackingError(true);
      toast.error(t("Mandoob:NewDelivery.TrackingRequired"));
      return false;
    }

    // The delivery calling phone must be valid whenever it is set; a template
    // that puts it in the message body makes it required.
    const normalizedDriverPhone = driverPhone.trim()
      ? normalizePhoneInput(driverPhone)
      : null;
    if (driverPhone.trim() && !normalizedDriverPhone) {
      setDriverPhoneError(true);
      toast.error(t("Mandoob:Form.InvalidPhone"));
      return false;
    }

    let variables: Record<string, string>;
    if (needs.mapped) {
      variables = buildTemplateVariables(template, {
        clientName: clientNameValue,
        trackingNumber: trackingValue,
        company: companyRaw,
        driverPhone: formatPhoneNumberForDisplay(driverPhone),
        driverName,
      })!;
    } else {
      variables = {};
      for (const name of template.variables)
        variables[name] = genericValues[name]?.trim() ?? "";
    }

    const name = clientNameValue.trim();
    const companyValue = companyRaw.trim();
    const driverNameValue = driverName.trim();
    startConversation.mutate({
      clientPhone: phone,
      ...(name ? { clientName: name } : {}),
      // Optional server-side; an empty value would fail min-length validation.
      ...(trackingValue.trim() ? { trackingNumber: trackingValue.trim() } : {}),
      ...(companyValue ? { company: companyValue } : {}),
      ...(driverNameValue ? { driverName: driverNameValue } : {}),
      ...(normalizedDriverPhone
        ? { driverCallingPhone: normalizedDriverPhone }
        : {}),
      ...overrides?.details,
      source: overrides?.details ? "Label" : "Manual",
      templateId: template.id,
      variables,
    });
    return true;
  };

  /**
   * Opens the CAMERA itself. Native: the Capacitor Camera plugin launches the
   * OS camera app directly (the file-input route shows a chooser/album on
   * Android WebViews). Web keeps the capture-hinted file input.
   */
  const captureLabel = async () => {
    // The label flow ends in an automatic template send, which costs a
    // credit — stop BEFORE the camera ever opens, not after the photo is
    // taken. Unconditional (even in verification builds): opening the camera
    // for a send that must fail wastes the driver's time and a Gemini call.
    const rechargeAction = {
      label: t("Mandoob:Plans.Buy"),
      onClick: () => void navigate({ to: "/plans" }),
    };
    if (
      !offerActive &&
      driver &&
      (driver.creditsBalance ?? 0) < TEMPLATE_SEND_COST
    ) {
      toast.error(t("Mandoob:Credits.InsufficientToast"), {
        action: rechargeAction,
      });
      return;
    }
    if (
      !offerActive &&
      !!driver?.creditsExpiresAt &&
      Date.parse(driver.creditsExpiresAt) <= Date.now()
    ) {
      toast.error(t("Mandoob:Credits.ExpiredToast"), {
        action: rechargeAction,
      });
      return;
    }
    if (!isNative()) {
      labelInputRef.current?.click();
      return;
    }
    try {
      const { Camera, CameraResultType, CameraSource } =
        await import("@capacitor/camera");
      const photo = await Camera.getPhoto({
        quality: 90,
        source: CameraSource.Camera,
        resultType: CameraResultType.Uri,
        correctOrientation: true,
      });
      if (!photo.webPath) return;
      const blob = await fetch(photo.webPath).then((res) => res.blob());
      const file = new File([blob], "label.jpg", {
        type: blob.type || "image/jpeg",
      });
      void handleLabelFile(file);
    } catch {
      // Camera dismissed — nothing to do.
    }
  };

  /** Camera shot -> Gemini fields -> form filled -> auto-send. */
  const handleLabelFile = async (file: File) => {
    setLabelLoading(true);
    try {
      // Client-side, like the chat input: JPEG, capped at 1500×1500.
      const prepared = await prepareLabelImage(file);
      const form = new FormData();
      form.append("file", prepared, prepared.name || "label.jpg");
      const data = await api<ShippingLabelDataDto>({
        url: "/api/inbox/label-extract",
        method: "POST",
        data: form,
      });
      // Labels often carry Arabic-Indic digits or decorated numbers — bring
      // them into the ASCII form the validators (and Meta) expect, so the
      // auto-send actually fires whenever the label had the data.
      const asciiDigits = (value: string) =>
        value
          .replace(/[٠-٩]/g, (d) => String("٠١٢٣٤٥٦٧٨٩".indexOf(d)))
          .replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d)));
      const phone = asciiDigits(data.phone ?? "").replace(/[^\d+]/g, "");
      const tracking = asciiDigits(data.trackingNumber ?? "").trim();
      // Mirror into the form so anything the model missed (or a failed send)
      // can be completed by hand.
      setClientPhone(phone);
      setClientName(data.name ?? "");
      setTrackingNumber(tracking);
      if (data.carrier) setCompany(data.carrier);
      setClientPhoneError(false);
      setTrackingError(false);

      labelModeRef.current = true;
      const started = handleSend({
        clientPhone: phone,
        clientName: data.name ?? "",
        trackingNumber: tracking,
        ...(data.carrier ? { company: data.carrier } : {}),
        details: labelDetails(data),
      });
      if (started) return; // spinner stays until the send settles
      labelModeRef.current = false;
    } catch (error) {
      toast.error(errorMessage(error, t));
    }
    setLabelLoading(false);
  };

  const optional = (
    <span className="tw:text-xs tw:font-normal tw:text-muted-foreground">
      ({t("Mandoob:Form.Optional")})
    </span>
  );

  const fieldLabel = (
    htmlFor: string,
    Icon: typeof Phone,
    text: string,
    isOptional?: boolean,
  ) => (
    <Label htmlFor={htmlFor} className="tw:flex tw:items-center tw:gap-1.5">
      <Icon className="tw:size-3.5 tw:shrink-0 tw:text-brand" strokeWidth={2} />
      {text} {isOptional ? optional : null}
    </Label>
  );

  // Full-screen template picker — a sub-screen of the same route.
  if (pickingTemplate) {
    return (
      <div
        dir={dir}
        data-no-swipe-back
        className="tw:flex tw:h-full tw:flex-col"
      >
        <header className="tw:shrink-0 tw:border-b tw:border-border">
          <div className="tw:mx-auto tw:w-full tw:flex tw:items-center tw:gap-3 tw:px-3 tw:pb-3 tw:pt-[calc(12px+env(safe-area-inset-top))] tw:md:max-w-2xl">
            <button
              type="button"
              onClick={() => setPickingTemplate(false)}
              aria-label={t("Mandoob:Back")}
              className="tw:flex tw:size-10 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:text-muted-foreground tw:transition-colors tw:hover:bg-muted"
            >
              <ArrowRight className="tw:size-5 tw:ltr:-scale-x-100" />
            </button>
            <div className="tw:flex tw:min-w-0 tw:flex-col">
              <h1 className="tw:m-0 tw:text-lg tw:font-semibold">
                {t("Mandoob:NewDelivery.TemplateStepTitle")}
              </h1>
              <p className="tw:m-0 tw:truncate tw:text-xs tw:text-muted-foreground">
                {t("Mandoob:NewDelivery.TemplateStepHint")}
              </p>
            </div>
          </div>
        </header>
        <div className="tw:mx-auto tw:min-h-0 tw:w-full tw:flex-1 tw:overflow-y-auto tw:p-4 tw:md:max-w-2xl tw:md:p-6">
          <div className="tw:flex tw:flex-col tw:gap-2">
            {templates.map((tpl) => {
              const selected = tpl.id === template?.id;
              return (
                <button
                  key={tpl.id}
                  type="button"
                  onClick={() => selectTemplate(tpl.id)}
                  className={cn(
                    "tw:flex tw:flex-col tw:gap-1 tw:rounded-xl tw:border tw:p-3 tw:text-start tw:text-foreground tw:transition-colors",
                    selected
                      ? "tw:border-brand-200 tw:bg-brand-50"
                      : "tw:border-border tw:bg-muted tw:hover:border-foreground/20",
                  )}
                >
                  <span className="tw:flex tw:items-center tw:gap-2">
                    {selected ? (
                      <Check
                        className="tw:size-4 tw:shrink-0 tw:text-brand"
                        strokeWidth={2.5}
                      />
                    ) : (
                      <FileText
                        className="tw:size-4 tw:shrink-0"
                        strokeWidth={1.75}
                      />
                    )}
                    <span className="tw:truncate tw:text-sm tw:font-semibold">
                      {formatTemplateName(tpl.name)}
                    </span>
                  </span>
                  {tpl.bodyText ? (
                    <span
                      className={cn(
                        "tw:text-xs tw:text-muted-foreground",
                        selected ? "tw:line-clamp-4" : "tw:line-clamp-2",
                      )}
                    >
                      <WhatsAppText text={filledBody(tpl)} />
                    </span>
                  ) : null}
                  {templateTags(tpl)}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div dir={dir} className="tw:flex tw:h-full tw:flex-col">
      <header className="tw:shrink-0 tw:border-b tw:border-border">
        <div className="tw:mx-auto tw:w-full tw:flex tw:items-center tw:gap-3 tw:px-3 tw:pb-3 tw:pt-[calc(12px+env(safe-area-inset-top))] tw:md:max-w-2xl">
          <button
            type="button"
            onClick={() => void navigate({ to: "/chats" })}
            aria-label={t("Mandoob:Back")}
            className="tw:flex tw:size-10 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:text-muted-foreground tw:transition-colors tw:hover:bg-muted"
          >
            <ArrowRight className="tw:size-5 tw:ltr:-scale-x-100" />
          </button>
          <h1 className="tw:m-0 tw:text-lg tw:font-semibold">
            {t("Mandoob:NewDelivery.Title")}
          </h1>
        </div>
      </header>

      <div className="tw:mx-auto tw:min-h-0 tw:w-full tw:flex-1 tw:overflow-y-auto tw:p-4 tw:md:max-w-2xl tw:md:p-6">
        <div className="tw:flex tw:flex-col tw:gap-4">
          {/* Template first: it decides which fields the rest of the form
              needs, and the label/bulk shortcuts below send with it. */}
          <Card>
            <CardHeader>
              <CardTitle className="tw:flex tw:items-center tw:gap-2 tw:text-sm">
                <FileText
                  className="tw:size-4 tw:shrink-0 tw:text-brand"
                  strokeWidth={1.75}
                />
                {t("Mandoob:NewDelivery.Template")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {templatesQuery.isLoading ? (
                <Skeleton className="tw:h-16 tw:rounded-xl" />
              ) : template ? (
                <button
                  type="button"
                  onClick={() => setPickingTemplate(true)}
                  aria-label={t("Mandoob:NewDelivery.ChangeTemplate")}
                  className="tw:flex tw:items-center tw:gap-3 tw:rounded-xl tw:border tw:border-border tw:bg-muted/50 tw:p-3 tw:text-start tw:transition-colors tw:hover:border-foreground/20"
                >
                  <span className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-0.5">
                    <span className="tw:flex tw:items-center tw:gap-2">
                      <span className="tw:truncate tw:text-sm tw:font-semibold tw:text-foreground">
                        {formatTemplateName(template.name)}
                      </span>
                    </span>
                    <span className="tw:line-clamp-2 tw:text-xs tw:text-muted-foreground">
                      <WhatsAppText text={filledBody(template)} />
                    </span>
                    {templateTags(template)}
                  </span>
                  <ChevronLeft className="tw:size-4 tw:shrink-0 tw:text-muted-foreground tw:ltr:-scale-x-100" />
                </button>
              ) : (
                <div className="tw:rounded-xl tw:border tw:border-border tw:bg-muted tw:p-3">
                  <p className="tw:m-0 tw:text-sm tw:font-semibold">
                    {t("Mandoob:Template.PickerEmpty")}
                  </p>
                  <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                    {t("Mandoob:Template.PickerEmptyHint")}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Bulk entry points, side by side: spreadsheet import and pick
              from the device contacts. */}
          <div className="tw:grid tw:grid-cols-2 tw:gap-3">
            <Link
              to="/chats/import"
              className="tw:flex tw:flex-col tw:gap-2 tw:rounded-xl tw:border tw:border-dashed tw:border-brand-200 tw:bg-brand-50 tw:p-3 tw:text-start tw:no-underline tw:transition-colors tw:hover:border-brand"
            >
              <span className="tw:flex tw:size-10 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-background tw:text-brand">
                <Sheet className="tw:size-5" strokeWidth={1.75} />
              </span>
              <span className="tw:text-sm tw:font-semibold tw:text-foreground">
                {t("Mandoob:NewDelivery.ImportCardTitle")}
              </span>
              <span className="tw:text-xs tw:text-muted-foreground">
                {t("Mandoob:NewDelivery.ImportCardSubtitle")}
              </span>
            </Link>
            <button
              type="button"
              onClick={() => void handlePickContacts()}
              disabled={pickingContacts || !contactsSourceAvailable()}
              className="tw:flex tw:flex-col tw:gap-2 tw:rounded-xl tw:border tw:border-dashed tw:border-brand-200 tw:bg-brand-50 tw:p-3 tw:text-start tw:transition-colors tw:hover:border-brand tw:disabled:opacity-60"
            >
              <span className="tw:flex tw:size-10 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-background tw:text-brand">
                {pickingContacts ? (
                  <Loader2 className="tw:size-5 tw:animate-spin" />
                ) : (
                  <BookUser className="tw:size-5" strokeWidth={1.75} />
                )}
              </span>
              <span className="tw:text-sm tw:font-semibold tw:text-foreground">
                {t("Mandoob:Import.FromContacts")}
              </span>
              <span className="tw:text-xs tw:text-muted-foreground">
                {contactsSourceAvailable()
                  ? t("Mandoob:Import.FromContactsHint")
                  : t("Mandoob:Import.ContactsMobileOnly")}
              </span>
            </button>
            {/* Label capture: full-width, horizontal — the flagship shortcut. */}
            <button
              type="button"
              onClick={() => void captureLabel()}
              disabled={labelLoading}
              className="tw:col-span-2 tw:flex tw:items-center tw:gap-3 tw:rounded-xl tw:border tw:border-dashed tw:border-brand-200 tw:bg-brand-50 tw:p-3 tw:text-start tw:transition-colors tw:hover:border-brand tw:disabled:opacity-60"
            >
              <span className="tw:flex tw:size-10 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-background tw:text-brand">
                <Camera className="tw:size-5" strokeWidth={1.75} />
              </span>
              <span className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col">
                <span className="tw:text-sm tw:font-semibold tw:text-foreground">
                  {t("Mandoob:Label.CardTitle")}
                </span>
                <span className="tw:text-xs tw:text-muted-foreground">
                  {t("Mandoob:Label.CardSubtitle")}
                </span>
              </span>
            </button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="tw:flex tw:items-center tw:gap-2 tw:text-sm">
                <Package
                  className="tw:size-4 tw:shrink-0 tw:text-brand"
                  strokeWidth={1.75}
                />
                {t("Mandoob:NewDelivery.SectionShipment")}
              </CardTitle>
            </CardHeader>
            <CardContent className="tw:flex tw:flex-col tw:gap-4">
              <div className="tw:flex tw:flex-col tw:gap-2">
                {fieldLabel(
                  "client-phone",
                  Phone,
                  t("Mandoob:NewDelivery.ClientPhone"),
                )}
                <Input
                  id="client-phone"
                  value={clientPhone}
                  onChange={(event) => {
                    setClientPhone(event.target.value);
                    setClientPhoneError(false);
                  }}
                  placeholder="05xxxxxxxx"
                  inputMode="tel"
                  dir="ltr"
                  autoComplete="tel"
                  aria-invalid={clientPhoneError || undefined}
                  className="tw:text-start"
                />
                <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                  {t("Mandoob:NewDelivery.PhoneHint")}
                </p>
              </div>

              {usesTracking && (
                <div className="tw:flex tw:flex-col tw:gap-2">
                  {fieldLabel(
                    "tracking-number",
                    Package,
                    t("Mandoob:Delivery.TrackingNumber"),
                  )}
                  <Input
                    id="tracking-number"
                    value={trackingNumber}
                    onChange={(event) => {
                      setTrackingNumber(event.target.value);
                      setTrackingError(false);
                    }}
                    placeholder={t("Mandoob:Delivery.TrackingPlaceholder")}
                    inputMode="text"
                    autoCapitalize="characters"
                    dir="ltr"
                    maxLength={100}
                    aria-invalid={trackingError || undefined}
                    className="tw:text-start"
                  />
                  {trackingError ? (
                    <p className="tw:m-0 tw:text-xs tw:text-destructive">
                      {t("Mandoob:NewDelivery.TrackingRequired")}
                    </p>
                  ) : null}
                </div>
              )}

              <div className="tw:flex tw:flex-col tw:gap-2">
                {fieldLabel(
                  "client-name",
                  User,
                  t("Mandoob:NewDelivery.ClientName"),
                  true,
                )}
                <Input
                  id="client-name"
                  value={clientName}
                  onChange={(event) => setClientName(event.target.value)}
                  placeholder={t("Mandoob:NewDelivery.ClientNamePlaceholder")}
                  maxLength={120}
                />
                <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                  {t("Mandoob:NewDelivery.ClientNameHint")}
                </p>
              </div>
            </CardContent>
          </Card>

          {hasMessageFields && (
            <Card>
              <CardHeader>
                <CardTitle className="tw:flex tw:items-center tw:gap-2 tw:text-sm">
                  <Truck
                    className="tw:size-4 tw:shrink-0 tw:text-brand"
                    strokeWidth={1.75}
                  />
                  {t("Mandoob:NewDelivery.SectionMessageDetails")}
                </CardTitle>
              </CardHeader>
              <CardContent className="tw:flex tw:flex-col tw:gap-4">
                {template && needs && !needs.mapped ? (
                  <div className="tw:flex tw:flex-col tw:gap-2.5">
                    {template.variables.map((name) => (
                      <label
                        key={name}
                        className="tw:flex tw:flex-col tw:gap-1"
                      >
                        <span className="tw:text-xs tw:font-semibold tw:text-muted-foreground">
                          {t("Mandoob:Template.Variable")} {name}
                        </span>
                        <Input
                          value={genericValues[name] ?? ""}
                          onChange={(event) =>
                            setGenericValues((prev) => ({
                              ...prev,
                              [name]: event.target.value,
                            }))
                          }
                        />
                      </label>
                    ))}
                  </div>
                ) : (
                  <>
                    {needs?.company && (
                      <div className="tw:flex tw:flex-col tw:gap-2">
                        {fieldLabel(
                          "delivery-company",
                          Building2,
                          t("Mandoob:Delivery.Company"),
                        )}
                        <Input
                          id="delivery-company"
                          value={company}
                          onChange={(event) => setCompany(event.target.value)}
                          maxLength={120}
                          autoComplete="organization"
                        />
                        <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                          {t("Mandoob:Delivery.CompanyHint")}
                        </p>
                      </div>
                    )}
                    {needs?.driverPhone && (
                      <div className="tw:flex tw:flex-col tw:gap-2">
                        {fieldLabel(
                          "delivery-calling-phone",
                          PhoneCall,
                          t("Mandoob:Delivery.CallingPhone"),
                        )}
                        <Input
                          id="delivery-calling-phone"
                          value={driverPhone}
                          onChange={(event) => {
                            setDriverPhone(event.target.value);
                            setDriverPhoneError(false);
                          }}
                          inputMode="tel"
                          dir="ltr"
                          autoComplete="tel"
                          aria-invalid={driverPhoneError || undefined}
                          className="tw:text-start"
                        />
                        {driverPhoneError ? (
                          <p className="tw:m-0 tw:text-xs tw:text-destructive">
                            {t("Mandoob:Form.InvalidPhone")}
                          </p>
                        ) : (
                          <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                            {t("Mandoob:Delivery.CallingPhoneHint")}
                          </p>
                        )}
                      </div>
                    )}
                    {needs?.driverName && (
                      <div className="tw:flex tw:flex-col tw:gap-2">
                        {fieldLabel(
                          "delivery-driver-name",
                          Truck,
                          t("Mandoob:Delivery.DriverName"),
                        )}
                        <Input
                          id="delivery-driver-name"
                          value={driverName}
                          onChange={(event) =>
                            setDriverName(event.target.value)
                          }
                          maxLength={120}
                        />
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {template ? (
            <Card>
              <CardHeader>
                <CardTitle className="tw:flex tw:items-center tw:gap-2 tw:text-sm">
                  <MessageSquare
                    className="tw:size-4 tw:shrink-0 tw:text-brand"
                    strokeWidth={1.75}
                  />
                  {t("Mandoob:Template.Preview")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="tw:m-0 tw:whitespace-pre-wrap tw:text-sm tw:text-foreground">
                  <WhatsAppText text={filledBody(template)} />
                </p>
              </CardContent>
            </Card>
          ) : null}
        </div>
      </div>

      <footer className="tw:shrink-0 tw:border-t tw:border-border">
        <div className="tw:mx-auto tw:w-full tw:flex tw:flex-col tw:gap-2 tw:p-4 tw:md:max-w-2xl">
          {!paymentsDisabled && (
            <span
              className={cn(
                "tw:inline-flex tw:items-center tw:justify-center tw:gap-1.5 tw:text-xs",
                hasCredits ? "tw:text-muted-foreground" : "tw:text-destructive",
              )}
            >
              <Coins className="tw:size-3.5" strokeWidth={1.75} />
              {offerActive
                ? t("Mandoob:Offer.FreeSend")
                : hasCredits
                  ? t("Mandoob:Template.Cost", TEMPLATE_SEND_COST)
                  : t("Mandoob:Credits.InsufficientToast")}
            </span>
          )}
          <Button
            type="button"
            onClick={() => handleSend()}
            disabled={!canSend}
            loading={startConversation.isPending}
            className="tw:w-full"
          >
            {t("Mandoob:NewDelivery.SendShipment")}
          </Button>
        </div>
      </footer>

      {/* Native contacts path: the whole book multi-select happens here; the
          confirmed selection is stashed and carried to the import flow. */}
      <ContactsPickerDialog
        open={contactsPickerOpen}
        contacts={deviceContacts}
        onClose={() => setContactsPickerOpen(false)}
        onConfirm={goToImportWith}
      />

      {/* capture="environment": the OS's own camera, not an in-app one. */}
      <input
        ref={labelInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="tw:hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file) void handleLabelFile(file);
        }}
      />

      {/* Reading the label — blocks the page while Gemini works. */}
      {labelLoading && (
        <div className="tw:fixed tw:inset-0 tw:z-50 tw:flex tw:flex-col tw:items-center tw:justify-center tw:gap-3 tw:bg-background/85 tw:backdrop-blur-sm">
          <Loader2 className="tw:size-10 tw:animate-spin tw:text-brand" />
          <p className="tw:m-0 tw:text-sm tw:font-medium tw:text-foreground">
            {t("Mandoob:Label.Reading")}
          </p>
        </div>
      )}

      {/* Delivery created from the label — scan the next one or wrap up. */}
      <ResponsiveDialog
        open={labelDoneOpen}
        onClose={() => setLabelDoneOpen(false)}
        title={t("Mandoob:Label.DoneTitle")}
        footer={
          <div className="tw:flex tw:w-full tw:gap-2">
            <Button
              type="button"
              variant="outline"
              className="tw:flex-1"
              onClick={() => {
                setLabelDoneOpen(false);
                setClientPhone("");
                setClientName("");
                setTrackingNumber("");
                setClientPhoneError(false);
                setTrackingError(false);
                void captureLabel();
              }}
            >
              <Camera className="tw:size-4.5" strokeWidth={1.75} />
              {t("Mandoob:Label.Another")}
            </Button>
            <Button
              type="button"
              className="tw:flex-1"
              onClick={() => void navigate({ to: "/chats" })}
            >
              {t("Mandoob:Label.Done")}
            </Button>
          </div>
        }
      >
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
          {t("Mandoob:Label.DoneText")}
        </p>
      </ResponsiveDialog>
    </div>
  );
}
